import React from 'react';
import { useCountry } from '../contexts/CountryContext';
import {
  Home, Search, CreditCard, UserCheck, MessageSquare, Key, KeyRound,
  Building2, Briefcase, CheckCircle, Gift, Sparkles, TrendingUp, Globe2,
  ShieldCheck, FileText, MapPin, BarChart3, MousePointerClick,
} from 'lucide-react';

export const usePlatformFees = () => {
  const { country, countryInfo, fees, freeTrial } = useCountry();
  const tenantNotes = { UK: 'Tenant Fees Act 2019', IN: 'Platform Access Fee', CA: 'Platform Access Fee' };
  return {
    buyer: fees.buyer,
    seller: fees.seller,
    sellerRent: fees.sellerRent,
    pro: fees.pro,
    tenant: fees.tenant,
    currency: countryInfo.symbol,
    country,
    tenantNote: tenantNotes[country] || 'Platform Policy',
    freeTrial,
  };
};

/**
 * Role-specific flows — "basic browsing guidance + fees & subscriptions" style.
 *
 * Each step is a concrete user action ("Open the AI Network panel and type a
 * plain-English brief"). When a step has an associated fee or subscription,
 * it appears as a `priceBadge` on the card so the cost is impossible to miss.
 *
 * The step icon + step number + role colour are preserved from the original
 * design so the look-and-feel stays consistent across the panel.
 */
export const getRoleFlows = (fees) => [
  {
    id: 'buyer',
    icon: <Home className="h-5 w-5" />,
    label: 'Buyers',
    color: '#E07A3A',
    intro: 'Buy with confidence — every listing AI-ranked, every seller verified.',
    steps: [
      {
        icon: <Search className="h-5 w-5" />,
        title: 'Browse the network',
        desc: 'Open the AI Network panel (left tab) and type a plain-English brief — "3-bed in Manchester under £350k with garden". Or browse city pages directly.',
        priceBadge: { label: 'Browsing', amount: 'FREE', tone: 'free' },
      },
      {
        icon: <UserCheck className="h-5 w-5" />,
        title: 'Get verified once',
        desc: 'One-time ID check turns you into a Pulse-Verified buyer for life. Verified sellers reply faster, and you only see vendor-verified stock.',
        priceBadge: { label: 'Buyer Verification', amount: `${fees.currency}${fees.buyer}`, sub: 'one-time' },
      },
      {
        icon: <MessageSquare className="h-5 w-5" />,
        title: 'Match & book viewings',
        desc: 'Save favourites, request viewings, chat directly with verified sellers. Optional Pulse Subscription auto-alerts you on new matches.',
        priceBadge: { label: 'Pulse Sub (optional)', amount: `${fees.currency}29`, sub: '/mo · cancel anytime' },
      },
      {
        icon: <Key className="h-5 w-5" />,
        title: 'Close the deal',
        desc: 'Use an in-network solicitor, order a Pulse Invest Report for the shortlist, and complete with AI chain visualisation.',
        priceBadge: { label: 'Pulse Invest Report', amount: `${fees.currency}49`, sub: 'one-off · optional' },
      },
    ],
  },
  {
    id: 'tenant',
    icon: <Key className="h-5 w-5" />,
    label: 'Tenants',
    color: '#22C55E',
    intro: 'Rent safely — deposit-scheme-protected landlords only.',
    steps: [
      {
        icon: <Search className="h-5 w-5" />,
        title: 'Browse verified rentals',
        desc: 'Open the AI Network panel, pick "Tenant", and enter your brief — budget, area, move-in date, pets. The map filters as you type.',
        priceBadge: { label: 'Browsing', amount: 'FREE', tone: 'free' },
      },
      {
        icon: fees.tenant > 0
          ? <UserCheck className="h-5 w-5" />
          : <CheckCircle className="h-5 w-5" />,
        title: fees.tenant > 0 ? 'Unlock tenant access' : 'Verify (free)',
        desc: fees.tenant > 0
          ? `One-time access fee unlocks every verified landlord on the network and stores your ID for instant referencing.`
          : `Free under the ${fees.tenantNote}. ID is checked and stored for one-tap referencing.`,
        priceBadge: fees.tenant > 0
          ? { label: 'Tenant Access', amount: `${fees.currency}${fees.tenant}`, sub: 'one-time' }
          : { label: 'Tenant Access', amount: 'FREE', tone: 'free', sub: fees.tenantNote },
      },
      {
        icon: <MessageSquare className="h-5 w-5" />,
        title: 'Book viewings',
        desc: 'In-app calendar — landlord confirms instantly. No email tag, no missed slots. Subscribe to Pulse Alerts to get new matches the moment they list.',
        priceBadge: { label: 'Pulse Alerts (optional)', amount: `${fees.currency}29`, sub: '/mo · cancel anytime' },
      },
      {
        icon: <KeyRound className="h-5 w-5" />,
        title: 'Sign & move in',
        desc: 'Auto-filled AST template using your verified ID. Deposit held in a government-approved scheme from day one. Keys in hand.',
        priceBadge: { label: 'Tenancy Pack', amount: 'INCLUDED', tone: 'included' },
      },
    ],
  },
  {
    id: 'seller',
    icon: <Building2 className="h-5 w-5" />,
    label: 'Sellers',
    color: '#3B82F6',
    intro: 'List free, route verified buyers to you, pay only on match. Two ways to close — direct or with a verified pro.',
    steps: [
      {
        icon: <Building2 className="h-5 w-5" />,
        title: 'List in 3 minutes',
        desc: 'Click "List Property" from your dashboard — upload photos, a 360° tour, and let our AI valuer suggest an asking price from local comps.',
        priceBadge: { label: 'Listing', amount: 'FREE', tone: 'free', sub: 'unlimited photos & tours' },
      },
      {
        icon: <Sparkles className="h-5 w-5" />,
        title: 'AI routes verified buyers',
        desc: 'Only ID-checked, Pulse-Verified buyers matching your criteria see your contact details. No time-wasters, no spam enquiries.',
        priceBadge: { label: 'Pulse Boost (optional)', amount: `${fees.currency}99`, sub: 'top-of-feed for 14 days' },
      },
      {
        icon: <CreditCard className="h-5 w-5" />,
        title: 'Pay only on match',
        desc: 'A one-time match fee triggers only when a verified buyer or tenant starts a real conversation with you. Until then — zero spend.',
        priceBadge: {
          label: 'Match fee',
          amount: `Sale ${fees.currency}${fees.seller} · Rent ${fees.currency}${fees.sellerRent}`,
          sub: 'per match · one-time',
        },
      },
      {
        icon: <MessageSquare className="h-5 w-5" />,
        title: 'Close directly',
        desc: 'Negotiate, exchange, complete — all in-thread. No middleman commission. Track everything in your Deals tab.',
        priceBadge: { label: 'Closing tools', amount: 'INCLUDED', tone: 'included' },
        // Backside content shown when the seller flips to the
        // "with a verified Pro" variant. Same 4-tile layout, but the
        // close step swaps "no middleman commission" for the
        // "select a verified professional" path.
        flip: {
          icon: <Briefcase className="h-5 w-5" />,
          title: 'Select a verified Pro',
          desc: 'Prefer hands-off? Pick a Pulse-Verified solicitor, surveyor or estate agent from your dashboard — they handle exchange, completion and paperwork end-to-end.',
          priceBadge: { label: 'Pro Match Fee', amount: `${fees.currency}${fees.pro}`, sub: 'one-time · paid to the pro' },
        },
      },
    ],
  },
  {
    id: 'investor',
    icon: <TrendingUp className="h-5 w-5" />,
    label: 'Investors · NRIs',
    color: '#F59E0B',
    intro: 'Buy by yield, not vibe — UK · India · Canada in one search.',
    steps: [
      {
        icon: <BarChart3 className="h-5 w-5" />,
        title: 'Filter by yield',
        desc: 'Open the AI Network panel, pick "Investor", set yield ≥ 8%, Pulse Score ≥ 75 and budget. Postcode-level yield maps render instantly.',
        priceBadge: { label: 'Yield search', amount: 'FREE', tone: 'free' },
      },
      {
        icon: <FileText className="h-5 w-5" />,
        title: 'Order Pulse Invest Report',
        desc: '28-page market intelligence — comps, rental data, RERA/RICS flags, 12-month outlook. Hard data, no hype.',
        priceBadge: { label: 'Pulse Invest Report', amount: `${fees.currency}49 · ₹4,900 · C$79`, sub: 'per report' },
      },
      {
        icon: <Globe2 className="h-5 w-5" />,
        title: 'Verify & reserve',
        desc: 'OCI-aware paperwork · RBI repatriation flags · multi-currency checkout (INR · GBP · CAD). Same buyer verification unlocks all three markets.',
        priceBadge: { label: 'Buyer Verification', amount: `${fees.currency}${fees.buyer}`, sub: 'one-time · all markets' },
      },
      {
        icon: <ShieldCheck className="h-5 w-5" />,
        title: 'Close cross-border',
        desc: 'Network-vetted solicitor in the target market. Power-of-attorney flow, escrow-ready transfers, and audit-grade closing pack.',
        priceBadge: { label: 'Closing pack', amount: 'INCLUDED', tone: 'included' },
      },
    ],
  },
  {
    id: 'professional',
    icon: <Briefcase className="h-5 w-5" />,
    label: 'Professionals',
    color: '#8B5CF6',
    intro: 'Pulse-Verified pros — pay only on warm intros, no subscription floor.',
    steps: [
      {
        icon: <ShieldCheck className="h-5 w-5" />,
        title: 'Register & verify',
        desc: 'Sign up as a solicitor, surveyor, mortgage adviser or letting agent. Upload your RICS / SRA / CLC / RERA / FCA credentials — we check & badge you.',
        priceBadge: { label: 'Registration', amount: 'FREE', tone: 'free' },
      },
      {
        icon: <Sparkles className="h-5 w-5" />,
        title: 'AI routes warm leads',
        desc: 'Live buyers & sellers in your postcode & specialism land straight in your inbox. Pre-qualified, ID-verified, ready to pay.',
        priceBadge: { label: 'City Subscription (optional)', amount: `${fees.currency}49`, sub: '/mo · top-of-directory listing' },
      },
      {
        icon: <CreditCard className="h-5 w-5" />,
        title: 'Pay per intro',
        desc: 'No subscription required. You pay the match fee only when a verified client engages — and you keep 100% of the underlying fee they pay you.',
        priceBadge: { label: 'Pro Match Fee', amount: `${fees.currency}${fees.pro}`, sub: 'per warm intro' },
      },
      {
        icon: <MessageSquare className="h-5 w-5" />,
        title: 'Earn & grow',
        desc: 'Top performers get featured inside the AI Network panel — every buyer & seller dashboard surfaces you. Affiliate Programme adds 20% recurring on referrals.',
        priceBadge: { label: 'Affiliate bonus', amount: '20%', sub: 'recurring · instant Stripe payouts' },
      },
    ],
  },
];

/**
 * Universal 4-step explainer — kept compact so the role cards below stay
 * the main visual focus.
 */
const SEARCH_MATCH_PILLARS = [
  { icon: <Search className="h-4 w-4" />,             title: 'Search',  desc: 'Plain-English search parses budget, area, beds, schools, yield — all at once.' },
  { icon: <Globe2 className="h-4 w-4" />,             title: 'Select',  desc: 'UK · India · Canada listings, verified pros, RERA & RICS tagged — refreshed daily.' },
  { icon: <Sparkles className="h-4 w-4" />,           title: 'Match',   desc: 'Every result gets a Pulse Score (0–100). Best fits surface first.' },
  { icon: <MousePointerClick className="h-4 w-4" />,  title: 'Connect', desc: 'Chat, book, unlock — most fees only trigger when a real match forms.' },
];

/**
 * Renders text with every occurrence of "optional" (any case) painted
 * amber + bold. Used inside price-badge labels and sub-labels so the
 * "you don't have to pay this" signal is impossible to miss.
 */
const Highlight = ({ children }) => {
  if (typeof children !== 'string') return children;
  const parts = children.split(/(\boptional\b)/gi);
  return parts.map((p, i) =>
    /^optional$/i.test(p)
      ? <span key={i} className="text-amber-300 font-bold">{p}</span>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
};

const PriceBadge = ({ badge, accent }) => {
  if (!badge) return null;
  const tone = badge.tone;
  const bg =
    tone === 'free'     ? 'bg-emerald-500/15 border-emerald-400/30 text-emerald-300'
    : tone === 'included' ? 'bg-white/[0.06] border-white/15 text-white/80'
    :                       'bg-white/[0.06] border-white/15 text-white';
  return (
    <div className={`mt-3 inline-flex flex-col items-center px-3 py-1.5 border ${bg} rounded`}>
      <span className="text-[9px] tracking-[0.18em] uppercase font-bold opacity-80"><Highlight>{badge.label}</Highlight></span>
      <span className="text-sm font-bold leading-none mt-0.5" style={tone || !accent ? undefined : { color: accent }}>
        {badge.amount}
      </span>
      {badge.sub && (
        <span className="text-[9px] tracking-wide opacity-60 mt-0.5"><Highlight>{badge.sub}</Highlight></span>
      )}
    </div>
  );
};

export const HowItWorksFlows = () => {
  const [activeRole, setActiveRole] = React.useState('buyer');
  const [sellerFlipped, setSellerFlipped] = React.useState(false);
  const fees = usePlatformFees();
  const flows = getRoleFlows(fees);
  const activeFlow = flows.find(r => r.id === activeRole);

  // Sellers get a 2-way toggle: "Sell direct" vs "With a verified Pro".
  // When the toggle is on the back, steps that have a `flip` payload
  // swap to that content — same 4-tile shape, role-themed colour.
  const renderedSteps = React.useMemo(() => {
    if (!activeFlow) return [];
    if (activeRole !== 'seller' || !sellerFlipped) return activeFlow.steps;
    return activeFlow.steps.map(s => (s.flip ? { ...s, ...s.flip } : s));
  }, [activeFlow, activeRole, sellerFlipped]);

  return (
    <div data-testid="how-it-works-flows">
      {/* Compact universal explainer */}
      <div className="mb-10" data-testid="search-match-explainer">
        <div className="text-center mb-5">
          <span className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-bold">
            How Search &amp; Match Works
          </span>
          <h3 className="font-['Playfair_Display'] text-xl text-white mt-2">
            Search → Select → Match → Connect
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          {SEARCH_MATCH_PILLARS.map((pillar, i) => (
            <div
              key={pillar.title}
              className="bg-white/[0.04] border border-white/10 rounded p-3"
              data-testid={`pillar-${i}`}
            >
              <div className="flex items-center gap-1.5 mb-1.5 text-[#F0A875]">
                {pillar.icon}
                <span className="text-[9px] tracking-[0.2em] uppercase font-bold">
                  {String(i + 1).padStart(2, '0')} · {pillar.title}
                </span>
              </div>
              <p className="text-[11px] text-white/55 leading-snug">{pillar.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Role tabs */}
      <div className="text-center mb-5">
        <span className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-bold">
          Pick your role
        </span>
        <h3 className="font-['Playfair_Display'] text-2xl text-white mt-2">
          Browse, match &amp; close — your way
        </h3>
      </div>
      <div className="flex flex-wrap justify-center gap-3 mb-6">
        {flows.map((role) => (
          <button
            key={role.id}
            onClick={() => setActiveRole(role.id)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold tracking-wider uppercase transition-all duration-300 border ${
              activeRole === role.id
                ? 'border-transparent text-white shadow-lg'
                : 'border-white/15 text-white/50 hover:border-white/30 hover:text-white/80'
            }`}
            style={activeRole === role.id ? { backgroundColor: role.color, boxShadow: `0 8px 24px -8px ${role.color}66` } : undefined}
            data-testid={`flow-tab-${role.id}`}
          >
            {role.icon}
            {role.label}
          </button>
        ))}
      </div>

      {activeFlow?.intro && (
        <p className="text-center text-white/65 text-sm mb-6 max-w-xl mx-auto" data-testid={`flow-intro-${activeRole}`}>
          {activeFlow.intro}
        </p>
      )}

      {/* Seller-only path toggle. Two ways to close — direct or with a
          verified pro. Each tile keeps its position; only the closing
          step content swaps to surface the alternative. */}
      {activeRole === 'seller' && (
        <div className="flex justify-center mb-8" data-testid="seller-path-toggle">
          <div className="inline-flex items-center bg-white/[0.05] border border-white/10 rounded p-1">
            <button
              onClick={() => setSellerFlipped(false)}
              className={`px-4 py-2 text-[10px] tracking-[0.18em] uppercase font-bold rounded transition-all ${
                !sellerFlipped ? 'bg-[#3B82F6] text-white shadow' : 'text-white/55 hover:text-white'
              }`}
              data-testid="seller-path-direct"
            >
              Sell direct (DIY)
            </button>
            <button
              onClick={() => setSellerFlipped(true)}
              className={`px-4 py-2 text-[10px] tracking-[0.18em] uppercase font-bold rounded transition-all ${
                sellerFlipped ? 'bg-[#3B82F6] text-white shadow' : 'text-white/55 hover:text-white'
              }`}
              data-testid="seller-path-pro"
            >
              With a verified Pro
            </button>
          </div>
        </div>
      )}

      {activeFlow && (
        <div className="grid md:grid-cols-4 gap-6 relative" data-testid={`flow-steps-${activeRole}`}>
          <div
            className="hidden md:block absolute top-16 left-[12.5%] right-[12.5%] h-px"
            style={{ background: `linear-gradient(to right, transparent, ${activeFlow.color}40, transparent)` }}
          />
          {renderedSteps.map((step, index) => (
            <div
              key={`${activeRole}-${sellerFlipped ? 'pro' : 'direct'}-${index}`}
              className="relative text-center group animate-fade-in-up"
              style={{ animationDelay: `${index * 0.08}s` }}
              data-testid={`flow-step-${activeRole}-${index}`}
            >
              <div className="relative z-10 w-28 h-28 mx-auto mb-5 flex flex-col items-center justify-center border border-white/10 bg-[#1A2744] group-hover:border-white/25 transition-all duration-300">
                <div
                  className="w-10 h-10 flex items-center justify-center mb-2"
                  style={{ backgroundColor: `${activeFlow.color}15`, color: activeFlow.color }}
                >
                  {step.icon}
                </div>
                <span className="text-xs font-semibold tracking-[0.2em]" style={{ color: activeFlow.color }}>
                  STEP {String(index + 1).padStart(2, '0')}
                </span>
              </div>
              <h3 className="font-['Playfair_Display'] text-lg text-white mb-2 font-semibold">{step.title}</h3>
              <p className="text-white/55 text-sm leading-relaxed font-medium max-w-[240px] mx-auto">{step.desc}</p>
              <PriceBadge badge={step.priceBadge} accent={activeFlow.color} />
            </div>
          ))}
        </div>
      )}

      {/* Headline fee summary — the same legal/at-a-glance summary the user
          knows from before, refreshed for clarity. */}
      {activeFlow && (
        <div className="mt-12 text-center">
          <div
            className="inline-flex flex-wrap items-center justify-center gap-3 px-6 py-3 border border-white/10 bg-[#1A2744] relative"
            data-testid="flow-fee-summary"
          >
            {fees.freeTrial ? (
              <span className="text-emerald-400 font-semibold text-sm tracking-wider">
                <Gift className="h-4 w-4 inline mr-2" />
                FREE TRIAL — All fees waived! Sign up and explore for free
              </span>
            ) : activeRole === 'tenant' ? (
              fees.tenant > 0 ? (
                <span className="text-white/70 text-sm tracking-wider">
                  Tenant Access: <span className="text-[#E07A3A] font-semibold">{fees.currency}{fees.tenant}</span>
                  <span className="mx-2 text-white/20">|</span>
                  Pulse Alerts <span className="text-amber-300 font-bold">(optional)</span>: <span className="text-[#E07A3A] font-semibold">{fees.currency}29/mo</span>
                </span>
              ) : (
                <span className="text-emerald-400 font-semibold text-sm tracking-wider">
                  <CheckCircle className="h-4 w-4 inline mr-2" />
                  COMPLETELY FREE — {fees.tenantNote}
                </span>
              )
            ) : activeRole === 'seller' ? (
              <span className="text-white/70 text-sm tracking-wider">
                Listing: <span className="text-emerald-400 font-semibold">FREE</span>
                <span className="mx-2 text-white/20">|</span>
                Sale match: <span className="text-[#E07A3A] font-semibold">{fees.currency}{fees.seller}</span>
                <span className="mx-2 text-white/20">|</span>
                Rental match: <span className="text-[#E07A3A] font-semibold">{fees.currency}{fees.sellerRent}</span>
                <span className="mx-2 text-white/20">|</span>
                Pulse Boost <span className="text-amber-300 font-bold">(optional)</span>: <span className="text-[#E07A3A] font-semibold">{fees.currency}99</span>
                {sellerFlipped && (
                  <>
                    <span className="mx-2 text-white/20">|</span>
                    Pro Match Fee: <span className="text-[#E07A3A] font-semibold">{fees.currency}{fees.pro}</span>
                  </>
                )}
              </span>
            ) : activeRole === 'buyer' ? (
              <span className="text-white/70 text-sm tracking-wider">
                Buyer Verification: <span className="text-[#E07A3A] font-semibold">{fees.currency}{fees.buyer}</span>
                <span className="mx-2 text-white/20">|</span>
                Pulse Sub <span className="text-amber-300 font-bold">(optional)</span>: <span className="text-[#E07A3A] font-semibold">{fees.currency}29/mo</span>
                <span className="mx-2 text-white/20">|</span>
                Pulse Report <span className="text-amber-300 font-bold">(optional)</span>: <span className="text-[#E07A3A] font-semibold">{fees.currency}49</span>
              </span>
            ) : activeRole === 'investor' ? (
              <span className="text-white/70 text-sm tracking-wider">
                Pulse Invest Report: <span className="text-[#E07A3A] font-semibold">£49 · ₹4,900 · C$79</span>
                <span className="mx-2 text-white/20">|</span>
                Buyer Verification: <span className="text-[#E07A3A] font-semibold">{fees.currency}{fees.buyer}</span>
              </span>
            ) : (
              <span className="text-white/70 text-sm tracking-wider">
                Registration: <span className="text-emerald-400 font-semibold">FREE</span>
                <span className="mx-2 text-white/20">|</span>
                Pro Match: <span className="text-[#E07A3A] font-semibold">{fees.currency}{fees.pro}</span> per warm intro
                <span className="mx-2 text-white/20">|</span>
                City Sub <span className="text-amber-300 font-bold">(optional)</span>: <span className="text-[#E07A3A] font-semibold">{fees.currency}49/mo</span>
              </span>
            )}
          </div>
          {/* Trust strip — consistent across every role */}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-white/40">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3 text-emerald-400" />
              Pulse-Verified network
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3 w-3 text-[#F0A875]" />
              UK · India · Canada
            </span>
            <span className="inline-flex items-center gap-1.5">
              <FileText className="h-3 w-3 text-white/55" />
              RERA · RICS · GDPR aware
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
